import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave } from '@fortawesome/free-solid-svg-icons';
import { clinicsService, type ClinicRoom } from '../../services/clinicsService';
import { GhostInput } from '../../components/ui/GhostInput';
import { GhostSwitch } from '../../components/ui/GhostSwitch';
import BtnTaratic from '../../components/ui/BtnTaratic';
import { useAppSelector } from '../../store/hooks';
import { useNotifications } from '../../components/ui/notifications';

interface ClinicsRoomModalProps {
  roomToEdit: ClinicRoom | null;
  onClose: () => void;
  onSuccess: () => void;
}

// Remove colorOptions if it's not used anymore, but keep if needed for history


const ClinicsRoomModal: React.FC<ClinicsRoomModalProps> = ({ roomToEdit, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const theme = useAppSelector((state) => state.ui.theme);
  const notifications = useNotifications();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color_code: '#3b82f6',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (roomToEdit) {
      setFormData({
        name: roomToEdit.name,
        description: roomToEdit.description || '',
        color_code: roomToEdit.color_code,
        is_active: roomToEdit.is_active !== undefined ? roomToEdit.is_active : true,
      });
    }
  }, [roomToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      const message = 'El nombre de la sala es obligatorio.';
      setError(message);
      notifications.warning('Revisa los datos', message);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (roomToEdit) {
        await clinicsService.updateClinicRoom(roomToEdit.id as string, formData);
        notifications.success('Sala actualizada', `${formData.name.trim()} se actualizó correctamente.`);
      } else {
        await clinicsService.createClinicRoom(formData);
        notifications.success('Sala creada', `${formData.name.trim()} se añadió correctamente.`);
      }
      onSuccess();
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Error al guardar la sala.';
      setError(message);
      notifications.error('No se pudo guardar la sala', typeof message === 'string' ? message : undefined);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-ui="clinics-room-modal-region-001" className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div data-ui="clinics-room-modal-region-002" className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div data-ui="clinics-room-modal-region-003" className="relative bg-[#0d1117] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-fade-in-up">
        {/* Header */}
        <div data-ui="clinics-room-modal-region-004" className="flex justify-between items-center p-6 border-b border-white/5 bg-white/5">
          <h2 className="text-xl font-medium text-white/90">
            {roomToEdit ? 'Editar Sala' : 'Nueva Sala'}
          </h2>
          <button 
            onClick={onClose}
            className="text-white/50 hover:text-white transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Body */}
        <div data-ui="clinics-room-modal-region-005" className="p-6 bg-black/20">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div data-ui="clinics-room-modal-region-006" className="p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-200 text-sm">
                {error}
              </div>
            )}

            <div data-ui="clinics-room-modal-region-007">
              <label className="block text-sm font-medium text-white/70 mb-1">Nombre de la Sala</label>
              <GhostInput
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej. Sala Principal, Consultorio 2"
                autoFocus
              />
            </div>

            <div data-ui="clinics-room-modal-region-008">
              <label className="block text-sm font-medium text-white/70 mb-1">Descripción / Servicios</label>
              <GhostInput
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Ej. Chequeos generales, Pediatría (usado por el Bot de IA)"
              />
            </div>

            <div data-ui="clinics-room-modal-region-009">
              <label className="block text-sm font-medium text-white/70 mb-1">Color</label>
              <div data-ui="clinics-room-modal-region-010" className="flex items-center gap-2">
                <input
                  type="color"
                  value={/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(formData.color_code) ? formData.color_code : '#888888'}
                  onChange={(e) => setFormData({ ...formData, color_code: e.target.value })}
                  className="w-10 h-10 p-0 border border-white/20 rounded bg-black cursor-pointer"
                  aria-label="Color de la Sala"
                  title="Color de la Sala"
                />
                <input
                  type="text"
                  value={formData.color_code}
                  onChange={(e) => setFormData({ ...formData, color_code: e.target.value })}
                  placeholder="#3b82f6"
                  className="flex-1 px-3 py-2 border border-white/20 bg-black text-white placeholder-white/50 rounded text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div data-ui="clinics-room-modal-region-011" className="flex items-center justify-between pt-2">
              <label htmlFor="is_active" className="text-sm font-medium text-white/80 cursor-pointer">
                Sala Activa (visible para agendar)
              </label>
              <div data-ui="clinics-room-active-switch">
                <GhostSwitch
                  id="is_active"
                  variant={theme}
                  checked={formData.is_active}
                  onChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  ariaLabel="Sala Activa (visible para agendar)"
                />
              </div>
            </div>

            {/* Actions */}
            <div data-ui="clinics-room-modal-region-013" className="flex justify-end gap-3 pt-6">
              <BtnTaratic data-ui="clinics-room-cancel-button" color="ghost-dark" onClick={onClose} type="button">
                {t('common.cancel')}
              </BtnTaratic>
              <BtnTaratic type="submit" loading={loading} color="green">
                <FontAwesomeIcon icon={faSave} className="mr-2" />
                {t('common.save')}
              </BtnTaratic>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClinicsRoomModal;
